import React, { useState } from 'react';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  Calendar,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminEmailTemplates } from '../../hooks/useAdminEmailTemplates';
import { formatDate } from '../../utils/dateUtils';

const AdminEmailTemplates = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const {
    templates,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    toggleStatus,
    isToggling,
    deleteTemplate,
    isDeleting
  } = useAdminEmailTemplates();

  // Define table columns
  const columns = [
    {
      key: 'template_name',
      label: 'Template Name',
      sortable: true,
      width: 'w-48',
      render: (template) => (
        <p className="text-sm font-medium text-gray-900">
          {template.template_name || 'N/A'}
        </p>
      )
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      width: 'w-64',
      render: (template) => (
        <p className="text-sm text-gray-900 truncate">
          {template.subject || 'N/A'}
        </p>
      )
    },
    {
      key: 'variables',
      label: 'Variables',
      sortable: false,
      width: 'w-48',
      render: (template) => (
        <div className="flex flex-wrap gap-1">
          {template.variables && Array.isArray(template.variables) && template.variables.length > 0 ? (
            template.variables.slice(0, 3).map((variable, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                {variable}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">No variables</span>
          )}
          {template.variables && template.variables.length > 3 && (
            <span className="text-xs text-gray-500">+{template.variables.length - 3} more</span>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (template) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(template.created_at, 'medium')}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-24',
      render: (template) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          template.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {template.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (template) => {
        setEditingTemplate(template);
        setShowCreateModal(true);
      }
    },
    {
      label: template => template.is_active ? 'Deactivate' : 'Activate',
      icon: template => template.is_active ? ToggleLeft : ToggleRight,
      variant: 'default',
      onClick: (template) => {
        toggleStatus(template.id);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (template) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
          deleteTemplate(template.id);
        }
      }
    }
  ];

  const { totalTemplates, activeTemplates, inactiveTemplates } = statistics;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Email Templates</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load email templates. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Email Templates
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage email templates for automated communications
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingTemplate(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Templates</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalTemplates}</p>
              </div>
              <Mail className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{activeTemplates}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">{inactiveTemplates}</p>
              </div>
              <XCircle className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={templates}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search templates by name, subject, or content..."
            onSearch={handleSearch}
            emptyMessage="No email templates found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;

